/**
 * Primitives shared by the §7 contracts.
 *
 * Every aggregate declares its schema FIRST and derives its type with `z.infer`
 * (AC-5) — one source, not two. A hand-written `interface` next to a schema drifts
 * the first time someone edits only one of them.
 *
 * ⚠️ Fields marked `// ⚠️ assumido — §9` are OUR assumption, not something the
 * backend confirmed. The marker is load-bearing: it is what a reviewer greps for
 * when the real contract finally lands.
 */
import { z } from 'zod'

/** `YYYY-MM-DD`. The backend has not stated a date format — this is the §7 shape. */
export const dataIso = z.string().regex(/^\d{4}-\d{2}-\d{2}$/, 'data deve estar em YYYY-MM-DD')

/** `YYYY-MM` — a competência / fiscal period. */
export const competencia = z
  .string()
  .regex(/^\d{4}-(0[1-9]|1[0-2])$/, 'competência deve estar em YYYY-MM')

/**
 * Money. **Always monthly** in the opportunity schema — Eduardo was explicit, and
 * the UI must label it so. Negative values are legal (a credit, an estorno).
 */
export const dinheiro = z.number().finite()

/** An identifier the backend owns. Never generated in the SPA. */
export const id = z.string().min(1)

/**
 * A value that comes from a **runtime catalog** (BR-24) — account id, serviço AWS,
 * status. It renders as a `<Select>` fed by the API and is **never free text** in
 * the UI, but on the wire it is an open string: the catalog is administered in
 * `Configurações globais` (2p) and may grow without a frontend deploy.
 *
 * Closing this into a `z.enum` is the exact mistake BR-24 exists to prevent — the
 * screen would hard-fail on a status an administrator added this morning.
 */
export const valorDeCatalogo = z.string().min(1)

/**
 * Widens a known union to "any string, but keep the autocomplete".
 *
 * `StatusOportunidade` must narrow exhaustively in a `switch` for the statuses we
 * know, while still parsing one we have never seen (AC-9). `string & {}` is what
 * buys both: TypeScript keeps the literal suggestions and stops collapsing the
 * union to plain `string`.
 */
export type CatalogoAberto<Conhecido extends string> = Conhecido | (string & {})
